import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import { CircularProgressbar } from 'react-circular-progressbar';
import 'react-circular-progressbar/dist/styles.css';
import { conseguirReporteGrupo } from '../../api/Groups';
import { Progress } from '../../interfaces/progress';
import ConstellationLayout from '../../components/ConstellationLayout';
import { useBreadcrumb } from '../../context/BreadcrumbContext';
import nene from '../../assets/nene.png';

type SkippedReasons = {
    not_in_core?: number;
    no_instance_months?: number;
    age_out_of_range?: number;
    no_stage_botnar?: number;
    no_session?: number;
};

type GroupReportResponse = {
    progress?: Progress[];
    num_users?: number;
    num_users_computed?: number;
    num_users_skipped?: number;
    skipped_reasons?: SkippedReasons;
    num_answers?: number;
    algorithm?: string;
};

const calculatePercentage = (total: number, completed: number) => {
    if (!total) return 0;
    return Number(((completed * 100) / total).toFixed(0));
};

const resolveAreaIcon = (imageUrl?: string | null): string | null => {
    if (!imageUrl) return null;
    const match = imageUrl.match(/([^/]+\.(?:png|jpg|jpeg|svg))$/i);
    if (!match) return null;
    return `/area-icons/${match[1]}`;
};

const styles = {
    page: {
        padding: '0',
        display: 'flex',
        justifyContent: 'center',
        width: '100%',
    } as React.CSSProperties,
    container: {
        width: '100%',
        maxWidth: 800,
    } as React.CSSProperties,
    header: {
        textAlign: 'center',
        marginBottom: '2.5rem',
    } as React.CSSProperties,
    babyImg: {
        width: 140,
        height: 'auto',
        marginBottom: '0.75rem',
    } as React.CSSProperties,
    groupName: {
        fontSize: '1.25rem',
        color: '#a1a4a5',
        margin: 0,
        fontWeight: 400,
    } as React.CSSProperties,
    metadataWrap: {
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        gap: '0.4rem',
        marginTop: '0.25rem',
        marginBottom: '1.5rem',
    } as React.CSSProperties,
    metadata: {
        fontSize: '0.9rem',
        color: '#a1a4a5',
    } as React.CSSProperties,
    infoWrap: {
        position: 'relative',
        display: 'inline-flex',
        alignItems: 'center',
        cursor: 'help',
    } as React.CSSProperties,
    infoIcon: {
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: 18,
        height: 18,
        borderRadius: '50%',
        border: '1px solid #a1a4a5',
        color: '#a1a4a5',
        fontSize: '0.75rem',
        fontWeight: 600,
        fontStyle: 'italic',
        lineHeight: 1,
    } as React.CSSProperties,
    tooltip: {
        position: 'absolute',
        bottom: 'calc(100% + 8px)',
        left: '50%',
        transform: 'translateX(-50%)',
        minWidth: 240,
        maxWidth: 320,
        backgroundColor: '#343a40',
        color: '#fff',
        padding: '0.75rem 1rem',
        borderRadius: 6,
        fontSize: '0.8rem',
        textAlign: 'left',
        boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
        zIndex: 10,
        whiteSpace: 'normal',
    } as React.CSSProperties,
    tooltipTitle: {
        fontWeight: 600,
        marginBottom: '0.4rem',
    } as React.CSSProperties,
    tooltipList: {
        margin: 0,
        paddingLeft: '1.2rem',
    } as React.CSSProperties,
    title: {
        fontSize: '2rem',
        fontWeight: 600,
        margin: 0,
    } as React.CSSProperties,
    subtitle: {
        textAlign: 'center',
        fontSize: '1.25rem',
        fontWeight: 500,
        marginTop: '1rem',
        marginBottom: '2rem',
    } as React.CSSProperties,
    areas: {
        display: 'flex',
        flexWrap: 'wrap',
        justifyContent: 'center',
        gap: '2.5rem',
    } as React.CSSProperties,
    area: {
        flex: '0 0 200px',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        textAlign: 'center',
    } as React.CSSProperties,
    chart: {
        width: 180,
        height: 180,
    } as React.CSSProperties,
    areaName: {
        marginTop: '1rem',
        fontSize: '1.1rem',
        fontWeight: 500,
    } as React.CSSProperties,
    areaIcon: {
        marginTop: '0.75rem',
        maxWidth: 48,
        maxHeight: 48,
    } as React.CSSProperties,
    centered: {
        display: 'flex',
        justifyContent: 'center',
        marginTop: '3rem',
    } as React.CSSProperties,
    spinner: {
        width: '4rem',
        height: '4rem',
    } as React.CSSProperties,
};

const GroupReportPage = () => {
    const { t } = useTranslation();
    const location = useLocation();
    const params = new URLSearchParams(location.search);
    const groupId = params.get('group_id');
    const groupName = params.get('name') || '';

    const [progress, setProgress] = useState<Progress[]>([]);
    const [numUsers, setNumUsers] = useState<number>(0);
    const [numUsersComputed, setNumUsersComputed] = useState<number>(0);
    const [numUsersSkipped, setNumUsersSkipped] = useState<number>(0);
    const [skippedReasons, setSkippedReasons] = useState<SkippedReasons>({});
    const [numAnswers, setNumAnswers] = useState<number>(0);
    const [loading, setLoading] = useState<boolean>(true);
    const [error, setError] = useState<string | null>(null);
    const [tooltipOpen, setTooltipOpen] = useState<boolean>(false);

    useEffect(() => {
        if (!groupId) {
            setError(t('group_report.missing_group'));
            setLoading(false);
            return;
        }
        (async () => {
            try {
                setLoading(true);
                const data: GroupReportResponse = await conseguirReporteGrupo(groupId);
                setProgress(Array.isArray(data?.progress) ? data.progress : []);
                setNumUsers(typeof data?.num_users === 'number' ? data.num_users : 0);
                setNumUsersComputed(typeof data?.num_users_computed === 'number' ? data.num_users_computed : 0);
                setNumUsersSkipped(typeof data?.num_users_skipped === 'number' ? data.num_users_skipped : 0);
                setSkippedReasons(data?.skipped_reasons || {});
                setNumAnswers(typeof data?.num_answers === 'number' ? data.num_answers : 0);
                setError(null);
            } catch (err: any) {
                const httpStatus = err?.response?.status;
                let msg: string;
                if (httpStatus === 501) {
                    msg = t('group_report.unsupported_algorithm');
                } else if (httpStatus === 503) {
                    msg = t('group_report.core_unavailable');
                } else if (!httpStatus) {
                    msg = t('group_report.error_connection');
                } else {
                    msg = t('group_report.error_status', { status: httpStatus });
                }
                console.error('[GroupReportPage] error fetching report:', err);
                setError(msg);
            } finally {
                setLoading(false);
            }
        })();
    }, [groupId, t]);

    const hasData = progress.length > 0;

    const breadcrumbItems = [
        ...(groupId
            ? [{ label: groupName || '', to: `/group?id=${groupId}&name=${encodeURIComponent(groupName)}` }]
            : []),
        { label: t('nav.report') },
    ];
    useBreadcrumb(breadcrumbItems);

    const skippedEntries = useMemo(() => {
        const entries: Array<{ key: string; label: string; count: number }> = [];
        const reasonKeys: Array<keyof SkippedReasons> = [
            'not_in_core',
            'no_instance_months',
            'age_out_of_range',
            'no_stage_botnar',
            'no_session',
        ];
        reasonKeys.forEach((key) => {
            const count = skippedReasons[key] || 0;
            if (count > 0) {
                entries.push({
                    key,
                    label: t(`group_report.skipped.${key}`),
                    count,
                });
            }
        });
        return entries;
    }, [skippedReasons, t]);

    const metadataText = numUsersSkipped > 0
        ? t('group_report.metadata_partial', {
              computed: numUsersComputed,
              total: numUsers,
              answers: numAnswers,
          })
        : t('group_report.metadata', {
              users: numUsers,
              answers: numAnswers,
          });

    return (
        <ConstellationLayout>
        <div style={styles.page}>
            <div style={styles.container}>
                <div style={styles.header}>
                    <img src={nene} alt="" style={styles.babyImg} />
                    {groupName && <h2 style={styles.groupName}>{groupName}</h2>}
                    {/* Metadata "X de Y niños · Z respuestas" + tooltip de skipped — oculto temporalmente por pedido del cliente
                    {!loading && !error && hasData && (
                        <div style={styles.metadataWrap}>
                            <span style={styles.metadata}>{metadataText}</span>
                            {numUsersSkipped > 0 && skippedEntries.length > 0 && (
                                <span
                                    style={styles.infoWrap}
                                    onMouseEnter={() => setTooltipOpen(true)}
                                    onMouseLeave={() => setTooltipOpen(false)}
                                    onClick={() => setTooltipOpen((v) => !v)}
                                >
                                    <span style={styles.infoIcon}>i</span>
                                    {tooltipOpen && (
                                        <div style={styles.tooltip} role="tooltip">
                                            <div style={styles.tooltipTitle}>
                                                {t('group_report.skipped_tooltip_title', { count: numUsersSkipped })}
                                            </div>
                                            <ul style={styles.tooltipList}>
                                                {skippedEntries.map((entry) => (
                                                    <li key={entry.key}>
                                                        {entry.count} {entry.label}
                                                    </li>
                                                ))}
                                            </ul>
                                        </div>
                                    )}
                                </span>
                            )}
                        </div>
                    )}
                    */}
                    <h1 style={styles.title}>{t('group_report.title')}</h1>
                </div>

                {error && (
                    <div className="alert alert-danger text-center">
                        {error}
                    </div>
                )}

                {loading && (
                    <div style={styles.centered}>
                        <div
                            className="spinner-border text-primary"
                            role="status"
                            style={styles.spinner}
                        >
                            <span className="sr-only">{t('group_report.loading')}</span>
                        </div>
                    </div>
                )}

                {!loading && !error && !hasData && (
                    <div className="alert alert-info text-center">
                        {t('group_report.no_data')}
                    </div>
                )}

                {!loading && !error && hasData && (
                    <>
                        <h3 style={styles.subtitle}>{t('group_report.subtitle')}</h3>
                        <div style={styles.areas}>
                            {progress.map((item: Progress) => {
                                const pct = calculatePercentage(item.area_total, item.completed);
                                const icon = resolveAreaIcon(item.area.image_url);
                                return (
                                    <div style={styles.area} key={item.area.id}>
                                        <div style={styles.chart}>
                                            <CircularProgressbar value={pct} text={`${pct}%`} />
                                        </div>
                                        <div style={styles.areaName}>{item.area.name}</div>
                                        {icon && (
                                            <img
                                                src={icon}
                                                alt={item.area.name}
                                                style={styles.areaIcon}
                                            />
                                        )}
                                    </div>
                                );
                            })}
                        </div>
                    </>
                )}
            </div>
        </div>
        </ConstellationLayout>
    );
};

export default GroupReportPage;
